import {
  IconArrowsExchange,
  IconBell,
  IconBuilding,
  IconClock,
  IconCreditCard,
  IconDeviceMobile,
  IconHelp,
  IconMessageCircle,
  IconShield,
} from '@tabler/icons-react'
import React from 'react'

// Business Basics Configuration
export interface BusinessBasicsConfig {
  storeName: string
  oemDesignation?: string
  address: {
    line1: string
    line2?: string
    city: string
    state: string
    zipCode: string
  }
  primaryPhone: string
  secondaryPhone?: string
  email: string
  website: string
  timezone: string
  /** When true, street address is not shown to customers (e.g. multi-location under one CRM). */
  suppressDealershipAddressForCustomers?: boolean
}

export type HolidayHourRuleMode = 'closed' | 'shortened'

export interface HolidayHourRule {
  mode: HolidayHourRuleMode
  openTime?: string
  closeTime?: string
}

export type HolidayId =
  | 'NEW_YEARS_DAY'
  | 'MLK_DAY'
  | 'PRESIDENTS_DAY'
  | 'MEMORIAL_DAY'
  | 'EASTER'
  | 'INDEPENDENCE_DAY'
  | 'LABOR_DAY'
  | 'VETERANS_DAY'
  | 'THANKSGIVING'
  | 'CHRISTMAS'

export interface SpecialHourEntry {
  date: string
  department: string
  openTime?: string
  closeTime?: string
  isClosed: boolean
  description?: string
}

/** Form-only row for holiday overrides; persisted as `holidayHourRules` on save. */
export interface HolidayRuleRow {
  holidayId: HolidayId
  mode: 'closed' | 'shortened'
  openTime?: string
  closeTime?: string
}

// Hours & Coverage Configuration
export interface HoursCoverageConfig {
  timezone: string
  regularHours: {
    sales: {
      department: string
      days: Array<{
        dayOfWeek: number // 0=Sunday, 1=Monday, etc.
        openTime: string
        closeTime: string
        isClosed: boolean
      }>
    }
  }
  quietHours: {
    enabled: boolean
    startTime: string
    endTime: string
  }
  /** Per catalog holiday: closed or shortened override; omit a holiday for regular weekly hours that day. */
  holidayHourRules?: Partial<Record<HolidayId, HolidayHourRule>>
  /** One-off overrides (any date); takes precedence over holiday hour rules and regular hours. */
  specialHours?: SpecialHourEntry[]
}

// Engagement Channels Configuration
export interface EngagementChannelsConfig {
  /** The dealership employee BDC AI acts as. Outbound email is sent under this id. */
  agentEmployeeId?: number
  sms: {
    enabled: boolean
    /** The dealership's SMS number. Inbound replies are attributed to the dealer by it. */
    smsPhoneNumber?: string
  }
  email: {
    enabled: boolean
  }
  voice?: {
    enabled: boolean
    voicemailDrop: boolean
  }
  preferredChannel: 'SMS' | 'EMAIL' | 'VOICE'
  /** What BDC AI does when dealer staff text or call a customer. Defaults to all_channels. */
  staffContactDisengagement?: 'off' | 'all_channels' | 'same_channel'
  expirationPeriod: number // hours - how long before a conversation is considered expired
}

// Conversation Style Configuration
export interface ConversationStyleConfig {
  tone: 'formal' | 'conversational'
  aiPersona: {
    name: string
    useStaffName: boolean
    staffMemberName: string
  }
  emojiUsage: 'none' | 'minimal' | 'moderate' | 'frequent'
  templates: {
    /** Paragraphs appended after AI welcome on SMS first contact (opt-in / disclosures) */
    greetings: string[]
  }
  messageDelay: {
    enabled: boolean
    minDelayMs: number
    maxDelayMs: number
  }
}

// Discovery (API: `conversation_flow` — only the fields below are stored on that document)
export interface ConversationFlowConfig {
  askForBudget: boolean
  askForTradeIn: boolean
  purchaseTimeline: boolean
  financingPreferences: boolean
  appointmentSuggestion: 'same_day' | 'next_day' | 'flexible'
}

/** Reminders form; API: `appointment_recovery` */
export interface MissedAppointmentRecoveryFormValues {
  enabled: boolean
  touch1DelayHours: number
  touch1DelayMinutes: number
  touch2DelayHours: number
  touch2DelayMinutes: number
}

/** Reminders form; API: `showroom_unsold_recovery` */
export interface ShowroomUnsoldRecoveryFormValues {
  enabled: boolean
  touch1DelayHours: number
  touch2DelayDays: number
}

/** Reminders form; API: `appointment_reminders` */
export interface AppointmentRemindersFormValues {
  enabled: boolean
  dayBeforeReminder: {
    enabled: boolean
    offsetMinutes: number
  }
  hourBeforeReminder: {
    enabled: boolean
    offsetMinutes: number
  }
  respectQuietHours: boolean
  proximityTime?: {
    enabled: boolean
    offsetMinutes: number
  }
}

export interface RemindersFollowUpFormValues {
  missedAppointmentRecovery: MissedAppointmentRecoveryFormValues
  showroomUnsoldRecovery: ShowroomUnsoldRecoveryFormValues
  appointmentReminders: AppointmentRemindersFormValues
}

// Inventory & Pricing Configuration
export interface InventoryPricingConfig {
  maxVehiclesToSuggest: number
}

// Handoff & Escalation Configuration
export interface HandoffEscalationConfig {
  handoffMessage: string
  escalationContacts: {
    salesManager: string
    financeManager: string
    serviceManager: string
  }
}

// Compliance & Risk Configuration
export interface ComplianceRiskConfig {
  optOutKeywords: string[]
  keywordBlacklist: string[]
}

// Prequalification / Credit Configuration
export interface PrequalificationConfig {
  enabled: boolean
  prequalificationLink?: string
  offerOnAppointmentBooked: boolean
  offerOnAffordabilityQuestion: boolean
}

export type StoreContextTopic = 'location_rooftop' | 'store_identity' | 'contact_routing' | 'visit_prep'

export const STORE_CONTEXT_TOPIC_ORDER: StoreContextTopic[] = [
  'location_rooftop',
  'store_identity',
  'contact_routing',
  'visit_prep',
]

export const STORE_CONTEXT_TOPIC_LABELS: Record<StoreContextTopic, string> = {
  location_rooftop: 'Multi-rooftop & location',
  store_identity: 'Store identity',
  contact_routing: 'Contact & routing',
  visit_prep: 'Visit preparation',
}

export const STORE_CONTEXT_ANSWER_MAX_LENGTH = 2000

export interface StoreContextEntry {
  id: string
  topic: StoreContextTopic
  answer: string
  enabled: boolean
}

export interface StoreContextConfig {
  entries: StoreContextEntry[]
}

// Union type for all configuration types
export type ConfigData =
  | BusinessBasicsConfig
  | HoursCoverageConfig
  | EngagementChannelsConfig
  | ConversationStyleConfig
  | ConversationFlowConfig
  | InventoryPricingConfig
  | HandoffEscalationConfig
  | ComplianceRiskConfig
  | PrequalificationConfig
  | StoreContextConfig

/**
 * Data for the "Reminders & Follow-Up" UI section. Loaded by the parent from three API keys
 * (not a single `configurationType` in the database).
 */
export type RemindersFollowUpSectionData = {
  appointmentRecovery: unknown | null
  showroomUnsoldRecovery: unknown | null
  appointmentReminders: unknown | null
}

// Configuration section metadata
export interface ConfigSection {
  id: string
  title: string
  description: string
  icon: React.ComponentType<{ size?: number; stroke?: number }>
  category: 'business' | 'communication' | 'workflow' | 'inventory' | 'compliance'
}

export const CONFIG_SECTIONS: ConfigSection[] = [
  {
    id: 'business_basics',
    title: 'Business Basics',
    description: 'Store information, contact details, and timezone',
    icon: IconBuilding,
    category: 'business',
  },
  {
    id: 'hours_coverage',
    title: 'Hours & Coverage',
    description: 'Business hours and quiet hours',
    icon: IconClock,
    category: 'business',
  },
  {
    id: 'store_context',
    title: 'Store FAQ',
    description: 'Authoritative answers for store-specific customer questions',
    icon: IconHelp,
    category: 'business',
  },
  {
    id: 'engagement_channels',
    title: 'Engagement Channels',
    description: 'SMS, email, voice settings and preferences',
    icon: IconDeviceMobile,
    category: 'communication',
  },
  {
    id: 'conversation_style',
    title: 'Conversation Style',
    description: 'Tone, formality, and brand voice settings',
    icon: IconMessageCircle,
    category: 'communication',
  },
  {
    id: 'conversation_flow',
    title: 'Discovery',
    description: 'Discovery goals, vehicle suggestions, and related flow',
    icon: IconArrowsExchange,
    category: 'workflow',
  },
  {
    id: 'reminders_follow_up',
    title: 'Reminders & Follow-Up',
    description: 'Appointment reminders, missed visit recovery, and showroom follow-up',
    icon: IconBell,
    category: 'workflow',
  },
  {
    id: 'compliance_risk',
    title: 'Compliance & Risk',
    description: 'Opt-out keywords and blacklists',
    icon: IconShield,
    category: 'compliance',
  },
  {
    id: 'prequalification',
    title: 'Prequalification / Credit',
    description: 'Soft credit pull configuration and triggers',
    icon: IconCreditCard,
    category: 'workflow',
  },
]
